<template>
  <div class="box collapsible-card" style="position: relative">
    <b-loading :is-full-page="false" v-model="loading"></b-loading>
    <div class="card-header-wrapper" @click="toggleCard">
      <h1 class="title card-title">Users</h1>
      <div class="header-actions-desktop">
        <b-button
          type="is-primary is-light"
          tag="router-link"
          :to="{ name: 'UserInvite' }"
          @click.native.stop
        >
          Invite New User
        </b-button>
      </div>
      <b-icon :icon="isExpanded ? 'chevron-up' : 'chevron-down'" class="toggle-icon"></b-icon>
    </div>
    <div class="header-actions-mobile" v-if="isExpanded">
      <b-button type="is-primary is-light" tag="router-link" :to="{ name: 'UserInvite' }" expanded>
        Invite New User
      </b-button>
    </div>
    <div class="card-content-wrapper" :class="{ 'is-collapsed': !isExpanded }">
      <hr />
      <div>
        <b-table
          :data="users"
          sort-icon="chevron-up"
          sort-icon-size="is-small"
          :default-sort="['name', 'asc']"
          striped
          :paginated="users.length > 10"
          per-page="10"
          hoverable
          mobile-cards
          mobile-breakpoint="1024px"
        >
          <b-table-column field="first_name" label="Name" sortable v-slot="props">
            {{ props.row.first_name }} {{ props.row.last_name }}
          </b-table-column>

          <b-table-column field="email" sortable label="Email" v-slot="props">
            {{ props.row.email }}
          </b-table-column>

          <b-table-column field="membership.is_manager" sortable label="Manager" v-slot="props">
            <b-icon
              :type="props.row.membership.is_manager ? 'is-success' : 'is-danger'"
              :icon="props.row.membership.is_manager ? 'check-circle' : 'close-circle'"
              size="is-small"
            />
          </b-table-column>
          <b-table-column
            field="membership.is_restricted"
            sortable
            label="Restricted"
            v-slot="props"
          >
            <b-icon
              :type="props.row.membership.is_restricted ? 'is-success' : 'is-danger'"
              :icon="props.row.membership.is_restricted ? 'check-circle' : 'close-circle'"
              size="is-small"
            />
          </b-table-column>
          <b-table-column
            field="membership.perm_reimbursements"
            sortable
            label="Can see reimbursements"
            v-slot="props"
          >
            <b-icon
              :type="props.row.membership.perm_reimbursements ? 'is-success' : 'is-danger'"
              :icon="props.row.membership.perm_reimbursements ? 'check-circle' : 'close-circle'"
              size="is-small"
            />
          </b-table-column>
          <b-table-column field="is_active" sortable label="Active" v-slot="props">
            <b-icon
              :type="props.row.is_active ? 'is-success' : 'is-danger'"
              :icon="props.row.is_active ? 'check-circle' : 'close-circle'"
              size="is-small"
            />
          </b-table-column>
          <b-table-column v-slot="props" numeric>
            <router-link size="is-small" :to="{ name: 'UserUpdate', params: { id: props.row.id } }">
              <b-icon icon="square-edit-outline" />
            </router-link>
          </b-table-column>
        </b-table>
      </div>
    </div>
  </div>
</template>

<script>
import request from '../request';

export default {
  name: 'UserList',
  data() {
    return {
      loading: true,
      users: [],
      isExpanded: window.innerWidth > 1024,
      isMobile: window.innerWidth <= 1024,
    };
  },
  mounted() {
    this.getUsers();
    window.addEventListener('resize', this.handleResize);
    this.handleResize();
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.handleResize);
  },
  methods: {
    toggleCard() {
      if (this.isMobile) {
        this.isExpanded = !this.isExpanded;
      }
    },
    handleResize() {
      this.isMobile = window.innerWidth <= 1024;
      if (!this.isMobile) {
        this.isExpanded = true;
      }
    },
    getUsers() {
      this.loading = true;
      request
        .get('/users')
        .then((resp) => (this.users = resp.data.users))
        .finally(() => (this.loading = false));
    },
  },
};
</script>

<style scoped>
.card-header-wrapper {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  padding: 0.5rem 0;
}

.card-title {
  margin-bottom: 0 !important;
  flex-grow: 1;
}

@media (max-width: 1024px) {
  .collapsible-card {
    border-left: 4px solid #7957d5;
  }

  .card-header-wrapper {
    min-height: 1.5rem;
    padding: 0.1rem 0;
  }

  .card-title {
    font-weight: 600 !important;
    color: #363636;
  }
}

.header-actions-desktop {
  flex-shrink: 0;
}

.header-actions-mobile {
  display: none;
}

.toggle-icon {
  display: none;
  color: #6c757d;
  flex-shrink: 0;
}

.card-content-wrapper {
  overflow: hidden;
  transition:
    max-height 0.3s ease,
    opacity 0.3s ease;
  max-height: 2000px;
  opacity: 1;
}

@media (max-width: 1024px) {
  .card-header-wrapper {
    cursor: pointer;
  }

  .card-title {
    font-size: 1.25rem !important;
  }

  .header-actions-desktop {
    display: none;
  }

  .header-actions-mobile {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
    margin-top: 1rem;
    margin-bottom: 0.5rem;
  }

  .toggle-icon {
    display: block;
  }

  .card-content-wrapper.is-collapsed {
    max-height: 0;
    opacity: 0;
    overflow: hidden;
  }

  /* Hide mobile sort dropdown */
  :deep(.table-mobile-sort) {
    display: none !important;
  }

  /* Force mobile card view */
  :deep(.b-table .table-wrapper) {
    overflow-x: visible;
  }

  :deep(.b-table .table) {
    display: block;
  }

  :deep(.b-table .table thead) {
    display: none;
  }

  :deep(.b-table .table tbody) {
    display: block;
  }

  :deep(.b-table .table tr) {
    display: block;
    margin-bottom: 1rem;
    border: 1px solid #dbdbdb;
    border-radius: 4px;
    padding: 0.75rem;
    background: #fff;
    box-shadow: 0 2px 3px rgba(10, 10, 10, 0.1);
  }

  :deep(.b-table .table td) {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 0.5rem 0;
    border: none;
    border-bottom: 1px solid #f0f0f0;
  }

  :deep(.b-table .table td:last-child) {
    border-bottom: none;
  }

  :deep(.b-table .table td::before) {
    content: attr(data-label);
    font-weight: 600;
    margin-right: 1rem;
    color: #363636;
  }
}
</style>
